import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  Dna, 
  Brain, 
  ChevronRight, 
  Zap, 
  Target, 
  Shield, 
  Microscope, 
  Activity, 
  Globe, 
  Users,
  Search,
  Lightbulb,
  Maximize2,
  ChevronDown,
  Mail,
  MousePointer2,
  ArrowRightCircle,
  Clock,
  Layout,
  Layers,
  Sparkles
} from 'lucide-react';

import AOS from "aos";
import "aos/dist/aos.css";

// --- Bubble Background Component ---
const BubbleBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let lava0;
    
    const ge1doot = {
      screen: {
        elem: null,
        callback: null,
        ctx: null,
        width: 0,
        height: 0,
        left: 0,
        top: 0,
        init: function (id, callback, initRes) {
          this.elem = canvas;
          this.callback = callback || null;
          this.ctx = ctx;
          window.addEventListener('resize', this.resize.bind(this), false);
          initRes && this.resize();
          return this;
        },
        resize: function () {
          const o = this.elem;
          if (!o) return;
          this.width = o.offsetWidth;
          this.height = o.offsetHeight;
          
          let left = 0, top = 0;
          let obj = o;
          while (obj) {
            left += obj.offsetLeft;
            top += obj.offsetTop;
            obj = obj.offsetParent;
          }
          this.left = left;
          this.top = top;
          
          if (this.ctx) {
            this.elem.width = this.width;
            this.elem.height = this.height;
          }
          this.callback && this.callback();
        }
      }
    };

    const Point = function(x, y) {
      this.x = x;
      this.y = y;
      this.magnitude = x * x + y * y;
      this.computed = 0;
      this.force = 0;
    };
    Point.prototype.add = function(p) {
      return new Point(this.x + p.x, this.y + p.y);
    };

    const Ball = function(parent) {
      const min = .1;
      const max = 1.5;
      this.vel = new Point(
        (Math.random() > 0.5 ? 1 : -1) * (0.2 + Math.random() * 0.25), (Math.random() > 0.5 ? 1 : -1) * (0.2 + Math.random())
      );
      this.pos = new Point(
        parent.width * 0.2 + Math.random() * parent.width * 0.6,
        parent.height * 0.2 + Math.random() * parent.height * 0.6
      );
      this.size = (parent.wh / 15) + ( Math.random() * (max - min) + min ) * (parent.wh / 15);
      this.width = parent.width;
      this.height = parent.height;
    };

    Ball.prototype.move = function() {
      if (this.pos.x >= this.width - this.size) {
        if (this.vel.x > 0) this.vel.x = -this.vel.x;
        this.pos.x = this.width - this.size;
      } else if (this.pos.x <= this.size) {
        if (this.vel.x < 0) this.vel.x = -this.vel.x;
        this.pos.x = this.size;
      }
      if (this.pos.y >= this.height - this.size) {
        if (this.vel.y > 0) this.vel.y = -this.vel.y;
        this.pos.y = this.height - this.size;
      } else if (this.pos.y <= this.size) {
        if (this.vel.y < 0) this.vel.y = -this.vel.y;
        this.pos.y = this.size;
      }
      this.pos = this.pos.add(this.vel);
    };

    const LavaLamp = function(width, height, numBalls, c0, c1) {
      this.step = 5;
      this.width = width;
      this.height = height;
      this.wh = Math.min(width, height);
      this.sx = Math.floor(this.width / this.step);
      this.sy = Math.floor(this.height / this.step);
      this.paint = false;
      this.metaFill = createRadialGradient(width, height, width, c0, c1);
      this.plx = [0, 0, 1, 0, 1, 1, 1, 1, 1, 1, 0, 1, 0, 0, 0, 0];
      this.ply = [0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 1, 1, 0, 1, 0, 1];
      this.mscases = [0, 3, 0, 3, 1, 3, 0, 3, 2, 2, 0, 2, 1, 1, 0];
      this.ix = [1, 0, -1, 0, 0, 1, 0, -1, -1, 0, 1, 0, 0, 1, 1, 0, 0, 0, 1, 1];
      this.grid = [];
      this.balls = [];
      this.iter = 0;
      this.sign = 1;

      for (let i = 0; i < (this.sx + 2) * (this.sy + 2); i++) {
        this.grid[i] = new Point(
          (i % (this.sx + 2)) * this.step, (Math.floor(i / (this.sx + 2))) * this.step
        );
      }
      for (let k = 0; k < numBalls; k++) {
        this.balls[k] = new Ball(this);
      }
    };

    LavaLamp.prototype.computeForce = function(x, y, idx) {
      let force;
      const id = idx || x + y * (this.sx + 2);
      if (x === 0 || y === 0 || x === this.sx || y === this.sy) {
        force = 0.6 * this.sign;
      } else {
        force = 0;
        const cell = this.grid[id];
        let i = 0;
        let ball;
        while (ball = this.balls[i++]) {
          force += ball.size * ball.size / (-2 * cell.x * ball.pos.x - 2 * cell.y * ball.pos.y + ball.pos.magnitude + cell.magnitude);
        }
        force *= this.sign;
      }
      this.grid[id].force = force;
      return force;
    };

    LavaLamp.prototype.marchingSquares = function(next) {
      const x = next[0];
      const y = next[1];
      const pdir = next[2];
      const id = x + y * (this.sx + 2);
      if (this.grid[id].computed === this.iter) return false;
      let dir, mscase = 0;
      for (let i = 0; i < 4; i++) {
        const idn = (x + this.ix[i + 12]) + (y + this.ix[i + 16]) * (this.sx + 2);
        let force = this.grid[idn].force;
        if ((force > 0 && this.sign < 0) || (force < 0 && this.sign > 0) || !force) {
          force = this.computeForce(x + this.ix[i + 12], y + this.ix[i + 16], idn);
        }
        if (Math.abs(force) > 1) mscase += Math.pow(2, i);
      }
      if (mscase === 15) return [x, y - 1, false];
      else {
        if (mscase === 5) dir = (pdir === 2) ? 3 : 1;
        else if (mscase === 10) dir = (pdir === 3) ? 0 : 2;
        else {
          dir = this.mscases[mscase];
          this.grid[id].computed = this.iter;
        }
        const ix = this.step / (
          Math.abs(Math.abs(this.grid[(x + this.plx[4 * dir + 2]) + (y + this.ply[4 * dir + 2]) * (this.sx + 2)].force) - 1) /
          Math.abs(Math.abs(this.grid[(x + this.plx[4 * dir + 3]) + (y + this.ply[4 * dir + 3]) * (this.sx + 2)].force) - 1) + 1
        );
        ctx.lineTo(
          this.grid[(x + this.plx[4 * dir]) + (y + this.ply[4 * dir]) * (this.sx + 2)].x + this.ix[dir] * ix,
          this.grid[(x + this.plx[4 * dir + 1]) + (y + this.ply[4 * dir + 1]) * (this.sx + 2)].y + this.ix[dir + 4] * ix
        );
        this.paint = true;
        return [x + this.ix[dir + 4], y + this.ix[dir + 8], dir];
      }
    };

    LavaLamp.prototype.renderMetaballs = function() {
      let i = 0, ball;
      while (ball = this.balls[i++]) ball.move();
      this.iter++;
      this.sign = -this.sign;
      this.paint = false;
      ctx.fillStyle = this.metaFill;
      ctx.beginPath();
      i = 0;
      while (ball = this.balls[i++]) {
        let next = [Math.round(ball.pos.x / this.step), Math.round(ball.pos.y / this.step), false];
        do { next = this.marchingSquares(next); } while (next);
        if (this.paint) {
          ctx.fill();
          ctx.closePath();
          ctx.beginPath();
          this.paint = false;
        }
      }
    };

    const createRadialGradient = function(w, h, r, c0, c1) {
      const gradient = ctx.createRadialGradient(w / 1, h / 1, 0, w / 1, h / 1, r);
      gradient.addColorStop(0, c0);
      gradient.addColorStop(1, c1);
      return gradient;
    };

    let animationId;
    const run = function() {
      animationId = requestAnimationFrame(run);
      ctx.clearRect(0, 0, screen.width, screen.height);
      lava0.renderMetaballs();
    };

    const screen = ge1doot.screen.init(null, null, true);
    lava0 = new LavaLamp(screen.width, screen.height, 6, "#FF9298", "#E4008E");
    run();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', screen.resize);
    };
  }, []);

  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden z-0 bg-gradient-to-br from-[#835EFF] to-[#FF008D]">
      <canvas ref={canvasRef} className="w-full h-full" />
    </div>
  );
};

// --- Custom Header Component ---
const CustomHero = () => {
  return (
    <header className="relative text-white flex flex-col items-center bg-black overflow-hidden pt-10 pb-0">
      <BubbleBackground />
      
      {/* Content Overlay */}
      <div className="relative z-10 flex flex-col items-center w-full px-4 text-center mt-20 md:mt-32 pb-32 md:pb-48">

        <h2 className="text-xl md:text-2xl lg:text-3xl font-medium max-w-4xl tracking-tight text-white/90 mb-4" data-aos="fade-up">
          Laboratorio inmersivo para investigar la <span className="bg-white/20 px-2 rounded-lg">salud cognitiva</span> y motriz
        </h2>
        
        <h1 className="text-2xl md:text-5xl lg:text-6xl text-white font-black max-w-6xl leading-[1.15] drop-shadow-2xl" data-aos="fade-up" data-aos-delay="200">
          Exploramos cómo la neurociencia, los videojuegos y la IA impulsan nuevas formas de detección y rehabilitación.
        </h1>
        
        <div className="home-impact-cta mt-16 mb-5" data-aos="fade-up" data-aos-delay="400">
          <Link to="/contacto" className="lit-up-button-impact scale-110">
            <div className="lit-up-border-impact" />
            <div className="lit-up-content-impact">
              Solicita información
            </div>
          </Link>
        </div>

      </div>

      {/* Custom Multi-Layered White Wave Divider */}
      <div className="absolute bottom-[-1px] left-0 w-full overflow-hidden leading-[0] z-20 pointer-events-none transform scale-y-[1.01]">
        <svg 
          viewBox="0 0 1440 150" 
          fill="none" 
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg" 
          className="relative block w-full h-[80px] md:h-[150px]"
          shapeRendering="geometricPrecision"
        >
          {/* Layer 1 - Softest transition */}
          <path 
            d="M0 150V20C200 60 400 100 720 100C1040 100 1240 60 1440 20V150H0Z" 
            fill="white"
            fillOpacity="0.2"
          />
          {/* Layer 2 - Middle layer */}
          <path 
            d="M0 150V40C250 80 450 120 720 120C990 120 1190 80 1440 40V150H0Z" 
            fill="white"
            fillOpacity="0.4"
          />
          {/* Layer 3 - Main solid layer */}
          <path 
            d="M0 150V60C300 100 500 140 720 140C940 140 1140 100 1440 60V150H0Z" 
            fill="white"
          />
        </svg>
      </div>

      <style>
        {`
          @keyframes rotate {
            100% { transform: rotate(1turn); }
          }
          @keyframes scroll {
            0% { transform: translateY(0); opacity: 1; }
            100% { transform: translateY(15px); opacity: 0; }
          }
          .animate-scroll {
            animation: scroll 1.5s ease-in-out infinite;
          }
        `}
      </style>
    </header>
  );
};

// --- El Reto Component ---
const ElReto = () => {
  return (
    <section id="el-reto" className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center mb-16" data-aos="fade-up">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ lineHeight: '1.3' }}>
            El reto global de la demencia
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-auto">
          {/* Box 1: 55M - Purple Theme */}
          <div className="col-span-1 md:col-span-2 bg-purple-50 p-8 md:p-10 rounded-[2.5rem] shadow-xl border border-purple-100 flex flex-col justify-center" data-aos="fade-up">
            <div className="flex flex-col md:flex-row items-center gap-6 mb-4">
              <div className="text-7xl md:text-8xl font-black bg-gradient-to-br from-purple-600 to-pink-500 bg-clip-text text-transparent">
                55M
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Personas afectadas mundialmente</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Más de 55 millones de personas viven con demencia en el mundo. Para 2050, la cifra podría triplicarse. El impacto económico supera el billón de dólares anuales.
                </p>
              </div>
            </div>
          </div>

          {/* Box 2: Tests convencionales */}
          <div className="col-span-1 bg-pink-50 p-8 rounded-[2.5rem] shadow-lg border border-pink-100 flex flex-col justify-center" data-aos="fade-up" data-aos-delay="100">
            <div className="bg-white w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm mb-6">
              <Lightbulb className="w-8 h-8 text-pink-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">Tests convencionales</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              Dependencia de personal especializado, alto consumo de tiempo, posibles sesgos por repetición y limitada recogida de datos en contextos reales.
            </p>
          </div>

          {/* Box 3: Biomarkers - Blue Theme */}
          <div className="col-span-1 md:col-span-2 bg-blue-50 p-8 md:p-10 rounded-[2.5rem] shadow-xl border border-blue-100 flex flex-col md:flex-row items-center gap-8 relative overflow-hidden" data-aos="fade-up" data-aos-delay="200">
            <Target className="absolute -top-10 -right-10 w-40 h-40 text-blue-200/50 rotate-12" />
            <div className="flex-shrink-0 bg-blue-600 p-5 rounded-3xl shadow-lg">
              <Target className="w-12 h-12 text-white" />
            </div>
            <div className="relative z-10">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Necesidad de biomarcadores tempranos</h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                Existe una creciente necesidad de desarrollar indicadores conductuales y cognitivos que permitan detectar el riesgo en fases preclínicas, facilitando intervenciones más eficaces y personalizadas.
              </p>
            </div>
          </div>

          {/* Box 4: Late Detection */}
          <div className="col-span-1 bg-purple-100/40 p-8 rounded-[2.5rem] shadow-lg border border-purple-200 flex flex-col justify-between" data-aos="fade-up" data-aos-delay="300">
            <div className="bg-white w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm mb-8">
              <Shield className="w-8 h-8 text-purple-600" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">Detección tardía</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                En muchos casos, la detección llega cuando el deterioro ya es avanzado. Los métodos tradicionales no permiten identificar cambios sutiles en fases iniciales.
              </p>
            </div>
          </div>

          {/* Box 5: Limitations */}
          <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-white p-8 md:p-12 rounded-[2.5rem] border border-gray-100 shadow-xl grid md:grid-cols-3 gap-10 items-center overflow-hidden relative" data-aos="fade-up" data-aos-delay="400">
             <div className="absolute top-0 right-0 w-64 h-64 bg-red-50 rounded-full blur-3xl opacity-50 -mr-20 -mt-20"></div>
             <div className="relative">
               <h3 className="text-xl md:text-2xl font-black uppercase tracking-tighter leading-none mb-2 text-red-600">Limitaciones</h3>
               <p className="text-gray-500 font-medium">de los métodos actuales</p>
             </div>
             <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 relative">
                {[
                  { text: "Dificultad para identificar alteraciones tempranas", color: "red" },
                  { text: "Evaluaciones alejadas del contexto real de la persona", color: "orange" },
                  { text: "Enfoques poco adaptables a necesidades individuales", color: "purple" }
                ].map((item, id) => (
                  <div key={id} className="flex flex-col gap-3 bg-gray-50 p-6 rounded-2xl border border-gray-100 hover:border-red-200 transition-all hover:shadow-lg group">
                    <span className="text-red-500 font-bold text-xl group-hover:scale-110 transition-transform inline-block">✕</span>
                    <span className="text-gray-700 text-sm font-semibold leading-tight">{item.text}</span>
                  </div>
                ))}
             </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Nuestra Solución Component ---
const NuestraSolucion = () => {
  const points = [
    {
      title: "Realidad Inmersiva",
      desc: "Evaluación y rehabilitación en entornos virtuales bajo evidencia científica.",
      icon: <Layers />
    },
    {
      title: "Inteligencia Artificial",
      desc: "Detección precoz mediante el análisis de patrones conductuales invisibles.",
      icon: <Brain />
    },
    {
      title: "Biometría y Datos",
      desc: "Monitorización fisiológica en tiempo real para un seguimiento preciso.",
      icon: <Activity />
    }
  ];

  return (
    <section id="nuestra-solucion" className="relative bg-black overflow-hidden py-24 md:py-32">
      <BubbleBackground />

      {/* Top Wave Divider (Flipped to point DOWN into the black section) */}
      <div className="absolute top-[-1px] left-0 w-full overflow-hidden leading-[0] z-20 pointer-events-none transform scale-y-[1.01]">
        <svg 
          viewBox="0 0 1440 150" 
          fill="none" 
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg" 
          className="relative block w-full h-[80px] md:h-[150px] rotate-180"
          shapeRendering="geometricPrecision"
        >
          <path d="M0 150V20C200 60 400 100 720 100C1040 100 1240 60 1440 20V150H0Z" fill="white" fillOpacity="0.2" />
          <path d="M0 150V40C250 80 450 120 720 120C990 120 1190 80 1440 40V150H0Z" fill="white" fillOpacity="0.4" />
          <path d="M0 150V60C300 100 500 140 720 140C940 140 1140 100 1440 60V150H0Z" fill="white" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-white">
        <div className="text-center mb-20" data-aos="fade-up">
          <h2 className="text-3xl md:text-5xl lg:text-5xl font-bold text-white mb-8 tracking-tighter">
            Nuestro Lab
          </h2>
          <p className="text-lg md:text-xl lg:text-2xl text-white font-bold max-w-4xl mx-auto leading-tight">
            Exploramos cómo la tecnología redefine el diagnóstico.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
          {points.map((p, i) => (
            <div key={i} className="flex flex-col items-center text-center px-4" data-aos="fade-up" data-aos-delay={i * 100}>
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-3xl bg-white/10 flex items-center justify-center mb-8 border border-white/20 shadow-2xl group hover:scale-110 transition-all duration-500 backdrop-blur-md">
                {React.cloneElement(p.icon, { className: "w-10 h-10 md:w-12 md:h-12 text-white" })}
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-white mb-4 tracking-tight">{p.title}</h3>
              <p className="text-lg md:text-xl text-white font-bold leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Wave Divider (Pointing UP into the black section) */}
      <div className="absolute bottom-[-1px] left-0 w-full overflow-hidden leading-[0] z-20 pointer-events-none transform scale-y-[1.01]">
        <svg 
          viewBox="0 0 1440 150" 
          fill="none" 
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg" 
          className="relative block w-full h-[80px] md:h-[150px]" // Standard rotation (points into the section)
          shapeRendering="geometricPrecision"
        >
          <path d="M0 150V20C200 60 400 100 720 100C1040 100 1240 60 1440 20V150H0Z" fill="white" fillOpacity="0.2" />
          <path d="M0 150V40C250 80 450 120 720 120C990 120 1190 80 1440 40V150H0Z" fill="white" fillOpacity="0.4" />
          <path d="M0 150V60C300 100 500 140 720 140C940 140 1140 100 1440 60V150H0Z" fill="white" />
        </svg>
      </div>
    </section>
  );
};

// --- Beneficios Component ---
const Beneficios = () => {
  const beneficios = [
    {
      title: 'Fase de investigación',
      description: 'Plataforma robusta para la investigación científica de vanguardia en detección temprana de demencia. Permite recopilar datos longitudinales de alta calidad.',
      icon: <Search className="w-12 h-12" />,
      color: "from-blue-500 to-blue-600"
    },
    {
      title: 'Rehabilitación motriz y cognitiva',
      description: 'Terapias inmersivas diseñadas específicamente para mejorar funciones cognitivas y habilidades motoras. Los entornos gamificados aumentan la motivación.',
      icon: <Brain className="w-12 h-12" />,
      color: "from-purple-500 to-purple-600"
    },
    {
      title: 'Hiperpersonalización',
      description: 'Algoritmos de IA adaptan dinámicamente cada sesión a las necesidades específicas del usuario según el rendimiento y progreso individual.',
      icon: <Zap className="w-12 h-12" />,
      color: "from-pink-500 to-pink-600"
    },
    {
      title: 'Datos en tiempo real',
      description: 'Captura continua de métricas biométricas y patrones de movimiento. Dashboards analíticos para una toma de decisiones informada.',
      icon: <Activity className="w-12 h-12" />,
      color: "from-indigo-500 to-indigo-600"
    },
  ];

  return (
    <section id="beneficios" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 bg-clip-text text-transparent" style={{ lineHeight: '1.3'}}>
            Beneficios que transforman
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto font-medium">
            Nuestro laboratorio ofrece ventajas únicas para investigadores, profesionales sanitarios y pacientes, creando un ecosistema integral de innovación.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {beneficios.map((beneficio, index) => (
            <div
              key={index}
              className="group relative bg-white p-8 rounded-[2rem] shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden flex flex-col items-center text-center"
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${beneficio.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500 blur-2xl`}></div>
              <div className={`mb-6 p-4 rounded-2xl bg-gradient-to-br ${beneficio.color} text-white shadow-lg transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3`}>
                {beneficio.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 leading-tight group-hover:text-blue-600 transition-colors">
                {beneficio.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {beneficio.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- Cómo Funciona Component ---
const ComoFunciona = () => {
  const pasos = [
    {
      numero: '01',
      title: 'Investigar',
      description: 'Establecemos fundamentos científicos rigurosos y colaboramos con la academia.',
      icon: <Search />
    },
    {
      numero: '02',
      title: 'Validar',
      description: 'Creamos entornos virtuales basados en evidencia con validación clínica.',
      icon: <Layers />
    },
    {
      numero: '03',
      title: 'Detectar',
      description: 'Sistemas de screening por IA que identifican patrones de deterioro precoz.',
      icon: <Brain />
    },
    {
      numero: '04',
      title: 'Seguridad',
      description: 'Espacios controlados sin riesgos físicos bajo supervisión remota.',
      icon: <Shield />
    },
  ];

  return (
    <section id="como-funciona" className="relative bg-black overflow-hidden py-24 md:py-32">
      <BubbleBackground />

      {/* Top Wave Divider (Flipped down) */}
      <div className="absolute top-[-1px] left-0 w-full overflow-hidden leading-[0] z-20 pointer-events-none transform scale-y-[1.01]">
        <svg 
          viewBox="0 0 1440 150" 
          fill="none" 
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg" 
          className="relative block w-full h-[80px] md:h-[150px] rotate-180"
          shapeRendering="geometricPrecision"
        >
          <path d="M0 150V20C200 60 400 100 720 100C1040 100 1240 60 1440 20V150H0Z" fill="white" fillOpacity="0.2" />
          <path d="M0 150V40C250 80 450 120 720 120C990 120 1190 80 1440 40V150H0Z" fill="white" fillOpacity="0.4" />
          <path d="M0 150V60C300 100 500 140 720 140C940 140 1140 100 1440 60V150H0Z" fill="white" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-white">
        <div className="text-center mb-20" data-aos="fade-up">
          <h2 className="text-3xl md:text-5xl lg:text-5xl font-bold text-white mb-8 tracking-tighter">
            Cómo funciona
          </h2>
          <p className="text-lg md:text-xl lg:text-2xl text-white font-bold max-w-4xl mx-auto leading-tight">
            Nuestra metodología combina investigación, diseño y tecnología de vanguardia.
          </p>
        </div>

        {/* Horizontal Timeline Layout */}
        <div className="relative">
          {/* Connector Line (Desktop) */}
          <div className="absolute top-10 md:top-12 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-white/40 hidden lg:block z-0"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 relative z-10">
            {pasos.map((paso, index) => (
              <div key={index} className="flex flex-col items-center text-center px-4 group" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="relative mb-8">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-3xl bg-white/10 flex items-center justify-center border border-white/20 shadow-2xl group-hover:scale-110 transition-all duration-500 backdrop-blur-md">
                    {React.cloneElement(paso.icon, { className: "w-10 h-10 md:w-12 md:h-12 text-white" })}
                  </div>
                  <div className="absolute -top-3 -right-3 w-10 h-10 rounded-full text-white text-xs font-black flex items-center justify-center shadow-2xl border-2 border-white backdrop-blur-sm">
                    {paso.numero}
                  </div>
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-white mb-4 tracking-tight">{paso.title}</h3>
                <p className="text-lg md:text-xl text-white font-bold leading-relaxed">{paso.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Wave Divider (Pointing up) */}
      <div className="absolute bottom-[-1px] left-0 w-full overflow-hidden leading-[0] z-20 pointer-events-none transform scale-y-[1.01]">
        <svg 
          viewBox="0 0 1440 150" 
          fill="none" 
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg" 
          className="relative block w-full h-[80px] md:h-[150px]"
          shapeRendering="geometricPrecision"
        >
          <path d="M0 150V20C200 60 400 100 720 100C1040 100 1240 60 1440 20V150H0Z" fill="white" fillOpacity="0.2" />
          <path d="M0 150V40C250 80 450 120 720 120C990 120 1190 80 1440 40V150H0Z" fill="white" fillOpacity="0.4" />
          <path d="M0 150V60C300 100 500 140 720 140C940 140 1140 100 1440 60V150H0Z" fill="white" />
        </svg>
      </div>
    </section>
  );
};

// --- Casos de Uso Component ---
const CasosDeUso = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const casos = [
    {
      question: 'Residencias de mayores',
      answer: 'Investigación de programas de detección temprana y mantenimiento cognitivo para residentes.',
    },
    {
      question: 'Clínicas de rehabilitación',
      answer: 'Desarrollamos y validamos protocolos de rehabilitación neurológica apoyados en tecnologías inmersivas para casos de ictus, traumatismos craneoencefálicos y enfermedades neurodegenerativas. Analizamos cómo los entornos virtuales facilitan la práctica funcional en escenarios controlados, mejorando la personalización y la toma de decisiones basada en evidencia.',
    },
    {
      question: 'Programas públicos de Silver Heath',
      answer: 'Trabajamos con iniciativas de salud pública para la población senior, desplegando estaciones de laboratorio en centros comunitarios que permiten realizar evaluaciones a gran escala.',
    },
  ];

  return (
    <section id="casos-de-uso" className="py-24 bg-white overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
        <div className="text-center mb-16" data-aos="fade-up">
          
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
            Casos de uso
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
            MoveSpark Lab se adapta a diversos contextos clínicos y comunitarios, ofreciendo soluciones específicas y escalables.
          </p>
        </div>

        <div className="w-full">
          {casos.map((caso, index) => (
            <div 
              className="border-b border-slate-200 py-6 cursor-pointer w-full group" 
              key={index} 
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <div className="flex items-center justify-between">
                <h3 className={`text-xl font-bold transition-colors duration-300 ${openIndex === index ? "text-blue-600" : "text-gray-900 group-hover:text-blue-500"}`}>
                  {caso.question}
                </h3>
                <svg width="22" height="22" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className={`${openIndex === index ? "rotate-180" : ""} transition-all duration-500 ease-in-out`}>
                  <path d="m4.5 7.2 3.793 3.793a1 1 0 0 0 1.414 0L13.5 7.2" stroke="currentColor" className={openIndex === index ? "text-blue-600" : "text-gray-400"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <p className={`text-lg text-gray-500 leading-relaxed transition-all duration-500 ease-in-out overflow-hidden ${openIndex === index ? "opacity-100 max-h-[500px] translate-y-0 pt-6" : "opacity-0 max-h-0 -translate-y-2"}`} >
                {caso.answer}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- CTA Final Component ---
const CTAFinal = () => {
  return (
    <section className="relative bg-black overflow-hidden py-20 md:py-24 -mb-1">
      <BubbleBackground />

      {/* Top Wave Divider (Flipped down) */}
      <div className="absolute top-[-1px] left-0 w-full overflow-hidden leading-[0] z-20 pointer-events-none transform scale-y-[1.01]">
        <svg 
          viewBox="0 0 1440 150" 
          fill="none" 
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg" 
          className="relative block w-full h-[60px] md:h-[120px] rotate-180"
          shapeRendering="geometricPrecision"
        >
          <path d="M0 150V20C200 60 400 100 720 100C1040 100 1240 60 1440 20V150H0Z" fill="white" fillOpacity="0.2" />
          <path d="M0 150V40C250 80 450 120 720 120C990 120 1190 80 1440 40V150H0Z" fill="white" fillOpacity="0.4" />
          <path d="M0 150V60C300 100 500 140 720 140C940 140 1140 100 1440 60V150H0Z" fill="white" />
        </svg>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-aos="zoom-in">
        <h2 className="text-3xl md:text-5xl lg:text-5xl font-bold text-white mb-8 tracking-tighter">
            Quiero investigar con MoveSpark Lab
          </h2>
        <p className="text-xl md:text-2xl font-bold mb-10 max-w-2xl mx-auto leading-tight" style={{ color: 'white' }}>
          Ya seas investigador, profesional sanitario o gestor de políticas públicas.
        </p>
        
        <div className="home-impact-cta mt-6" data-aos="fade-up">
          <Link to="/about" className="lit-up-button-impact scale-110">
            <div className="lit-up-border-impact" />
            <div className="lit-up-content-impact">
              Solicita más información
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
};

// --- Main Page Component ---
const MoveSparkPage = () => {
  useEffect(() => {
    AOS.init({ duration: 800, once: true, easing: 'ease-out-cubic' });
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="movespark-page-new selection:bg-purple-100 selection:text-purple-900 bg-white">
      <CustomHero />
      <ElReto />
      <NuestraSolucion />
      <Beneficios />
      <ComoFunciona />
      <CasosDeUso />
      <CTAFinal />
    </div>
  );
};

export default MoveSparkPage;
